import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { API } from '@/api'
import { CreateTeamRes } from '@/api/type-response'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { Divider } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { message } from '@/components/base/Notification'
import { PrivilegeType } from '@/consts/team'
import { Team } from '@/models/team'
import { useTeamStore } from '@/stores/team'

interface TeamCreatedSuccessModalProps {
  open: boolean
  onClose: () => void
  teamData: CreateTeamRes['team'] | null
}

export function TeamCreatedSuccessModal({ open, onClose, teamData }: TeamCreatedSuccessModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const teamStore = useTeamStore()
  const { t } = useTranslation('team')

  if (!teamData) return null

  const handleCopyAccount = () => {
    navigator.clipboard.writeText(teamData.adminName)
    message.success(t('success.accountCopied'))
  }

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(teamData.adminPassword)
    message.success(t('success.passwordCopied'))
  }

  const handleLoginToTeam = async () => {
    if (!teamData) return

    setIsLoading(true)
    try {
      // Step 1: Get team list (using platform token)
      const teamsRes = await API.getTeamList({ page: 1, per_page: 100 })

      // Step 2: Construct Team objects
      const teams = teamsRes.teams.map((item) => new Team(item))

      // Step 3: Find the newly created team by resID
      const team = teams.find((t) => t.resID === teamData.resID)

      if (!team) {
        message.error(t('success.teamNotFound'))
        setIsLoading(false)
        return
      }

      // Step 4: Get team token (15 minutes)
      const tokenRes = await API.getTeamToken({
        privilege_type: PrivilegeType.TeamToken,
        source_id: team.id,
      })

      // Step 5: Set team context (teamTokenData only, not primary)
      const { TEAM_TOKEN_EXPIRY_MS } = await import('@/utils/auth')
      teamStore.setTeamContext({
        token: `Bearer ${tokenRes.token}`,
        teamId: team.id,
        teamResID: team.resID,
        expiryTime: Date.now() + TEAM_TOKEN_EXPIRY_MS,
        userName: team.user.name,
      })

      // Step 6: Refresh page to switch to team context
      sessionStorage.setItem('__team_switching__', '1')
      window.location.href = '/team/home'
    } catch (error) {
      console.error('Failed to switch to team:', error)
      message.error(t('success.loginFailed'))
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[800px]">
      <Modal.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h6">{t('success.title')}</span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Description */}
          <p className="t-sb">
            <Trans
              i18nKey="success.description"
              ns="team"
              components={{
                highlight: <span className="text-orange-500" />,
              }}
            >
              The new team has been created successfully. Please note that the admin name and
              password are shown only once. Make sure to save them securely.
            </Trans>
          </p>

          <div className="bg-neutral-50 rounded-lg">
            {/* Basic Information */}
            <div className="bg-neutral-50 rounded-lg px-4 py-3 space-y-4">
              <div className="flex gap-2 items-center">
                <h3 className="t-xs text-tertiary flex-1 text-nowrap">{t('success.basicInfo')}</h3>
                <Divider className="my-0" />
              </div>

              <div className="grid grid-cols-2 gap-4 text-secondary">
                <div>
                  <div className="t-xs mb-1">{t('success.teamId')}</div>
                  <div className="t-sb text-primary">{teamData.resID}</div>
                </div>
                <div>
                  <div className="t-xs mb-1">{t('success.teamName')}</div>
                  <div className="t-sb text-primary">{teamData.teamName}</div>
                </div>
              </div>

              <div>
                <div className="t-xs mb-1">{t('success.domainName')}</div>
                <div className="t-sb text-primary">{teamData.domainName}</div>
              </div>
            </div>

            {/* Login Information */}
            <div className="bg-neutral-50 rounded-lg px-4 py-3 space-y-4">
              <div className="flex gap-2 items-center">
                <h3 className="t-xs text-tertiary text-nowrap">{t('success.loginInfo')}</h3>
                <Divider className="my-0" />
              </div>

              <div className="grid grid-cols-2 gap-4 text-secondary">
                <div>
                  <div className="t-xs mb-1">{t('success.loginAccount')}</div>
                  <div className="flex items-center gap-2">
                    <div className="t-sb text-primary">{teamData.adminName}</div>
                    <button
                      onClick={handleCopyAccount}
                      className="p-1 hover:bg-neutral-200 rounded transition-colors"
                      title={t('success.copyAccount')}
                    >
                      <CopyIcon className="w-4 h-4 text-btn-secondary" />
                    </button>
                  </div>
                </div>
                <div>
                  <div className="t-xs mb-1">{t('success.loginPassword')}</div>
                  <div className="flex items-center gap-2">
                    <div className="t-sb text-primary">{teamData.adminPassword}</div>
                    <button
                      onClick={handleCopyPassword}
                      className="p-1 hover:bg-neutral-200 rounded transition-colors"
                      title={t('success.copyPassword')}
                    >
                      <CopyIcon className="w-4 h-4 text-btn-secondary" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Login Tips */}
            <div className="bg-neutral-50 rounded-lg px-4 py-3 space-y-4">
              <div className="flex gap-2 items-center">
                <h3 className="t-xs text-tertiary text-nowrap">{t('success.loginTips')}</h3>
                <Divider className="my-0" />
              </div>

              <div className="t-sb text-primary space-y-2">
                <p>
                  <Trans
                    i18nKey="success.loginTipsDesc"
                    ns="team"
                    components={{
                      glowsLink: (
                        <a
                          href="https://glows.ai"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-btn-secondary hover:underline"
                        />
                      ),
                    }}
                  />
                </p>
                <p>{t('success.loginTipsOptions')}</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>
                    <Trans
                      i18nKey="success.loginTipsOption1"
                      ns="team"
                      components={{
                        glowsLink: (
                          <a
                            href="https://glows.ai"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-btn-secondary hover:underline"
                          />
                        ),
                      }}
                    />
                  </li>
                  <li>
                    <Trans
                      i18nKey="success.loginTipsOption2"
                      ns="team"
                      components={{
                        loginLink: (
                          <a
                            href="https://platform.glows.ai/team/login"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-btn-secondary hover:underline"
                          />
                        ),
                      }}
                    />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-4">
          <Button type="outline" onClick={onClose} disabled={isLoading}>
            {t('common.close')}
          </Button>
          <Button type="secondary" onClick={handleLoginToTeam} disabled={isLoading}>
            {t('success.enterTeam')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
